
import React from "react";
import { useBreakpoint, Breakpoint, useActiveBreakpoint } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

type ResponsiveProps = {
  children: React.ReactNode;
  className?: string;
  breakpoints?: {
    [key in Breakpoint]?: string;
  };
  defaultClassName?: string;
};

export function ResponsiveWrapper({
  children,
  className,
  breakpoints = {},
  defaultClassName = "",
}: ResponsiveProps) {
  const activeBreakpoint = useActiveBreakpoint();
  
  const getClassNames = () => {
    // If no active breakpoint or no matching breakpoint class, use default
    if (!activeBreakpoint || !breakpoints[activeBreakpoint]) {
      return defaultClassName;
    }
    
    return breakpoints[activeBreakpoint];
  };

  return (
    <div className={cn(getClassNames(), className)}>
      {children}
    </div>
  );
}

export function HideOnMobile({ children, className }: { children: React.ReactNode; className?: string }) {
  const isMobile = !useBreakpoint("md");
  if (isMobile) return null;
  return <div className={className}>{children}</div>;
}

export function ShowOnMobile({ children, className }: { children: React.ReactNode; className?: string }) {
  const isMobile = !useBreakpoint("md");
  if (!isMobile) return null;
  return <div className={className}>{children}</div>;
}

export function ResponsiveGrid({ 
  children, 
  className,
  cols = { 
    default: 1,
    sm: 1,
    md: 2,
    lg: 3,
    xl: 4,
    "2xl": 4
  }
}: { 
  children: React.ReactNode; 
  className?: string;
  cols?: {
    default: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    "2xl"?: number;
  }
}) {
  const getGridCols = () => {
    const colClasses = [
      `grid-cols-${cols.default}`,
      cols.sm ? `sm:grid-cols-${cols.sm}` : '',
      cols.md ? `md:grid-cols-${cols.md}` : '',
      cols.lg ? `lg:grid-cols-${cols.lg}` : '',
      cols.xl ? `xl:grid-cols-${cols.xl}` : '',
      cols["2xl"] ? `2xl:grid-cols-${cols["2xl"]}` : '',
    ];
    
    return colClasses.filter(Boolean).join(' ');
  };
  
  return (
    <div className={cn("grid gap-4", getGridCols(), className)}>
      {children}
    </div>
  );
}

export function ResponsiveContainer({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("w-full mx-auto max-w-7xl", className)}>
      {children}
    </div>
  );
}
